import React, { useState } from 'react';

import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { PaymentModel } from '../../../../../shared/models/payment.model';
import { PaymentGateway } from '../../../../../shared/enums/payment-gateway.enum';
import { PaymentVatBreakdownModal } from '../payment-vat-breakdown-modal/payment-vat-breakdown-modal.component';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import { hasAccess } from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import { ErrorInfo, isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import {
  PaymentUIModel,
  SplitPaymentModel
} from '../../../../../shared/ui-models/payment.ui-model';
import { PaymentOccurrenceMode } from '../../../../../shared/enums/payment-occurrence-mode.enum';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import {
  checkPaymobPaymentStatus,
  createOnePayment,
  generatePaymentLink,
  movePayment,
  sendPayment,
  uploadPaymentReceipt,
  updateOneReservations,
  updateOnePayment
} from '../../../../shared/services/data.service';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import { AddPaymentModal } from '../add-payment-modal/add-payment-modal.component';
import { SplitPaymentModal } from '../split-payment-modal/split-payment-modal.component';
import { GeneratePaymentLinkModal } from '../generate-payment-link-modal/generate-payment-link-modal.component';
import { SendPaymentLinkModal } from '../send-payment-link-modal/send-payment-link-modal.component';
import { AddPaymentReceiptModal } from '../add-payment-receipt-modal/add-payment-receipt-modal.component';
import { AddPartialPaymentModal } from '../add-partial-payment-modal/add-partial-payment-modal.component';
import NumberFormat from 'react-number-format';
import { HugeiconsIcon } from '@hugeicons/react';
import { Add01Icon } from '@hugeicons/core-free-icons';
import { PaymentHistoryModal } from '../payment-history-modal/payment-history-modal.component';
import { PaymentDetailsModal } from '../payment-details-modal/payment-details-modal.component';
import { SwitchGatewayModal } from '../switch-gateway-modal/switch-gateway-modal.component';
import { MovePaymentModal } from '../move-payment-modal/move-payment-modal.component';
import { ResendGuestEmailModal } from '../resend-guest-email-modal/resend-guest-email-modal.component';
import { PaymentsTable } from './payments-table.component';

interface PaymentsSectionProps {
  data: {
    reservation: ReservationDetailsUiModel;
    contactEmail: string;
  };
  config: {
    isPermited: (roles: UserRole[]) => boolean;
    userRole: any;
    userRoles: any;
  };
  eventHandlers: {
    onGetOneReservation: (id: string) => Promise<any>;
  };
}

export const PaymentsSection: React.FC<PaymentsSectionProps> = ({
  data: { reservation, contactEmail },
  config: { isPermited },
  eventHandlers: { onGetOneReservation }
}) => {
  const [showAddPaymentModal, setShowAddPaymentModal] = useState<boolean>(false);
  const [showGeneratePaymentLinkModal, setShowGeneratePaymentLinkModal] = useState<boolean>(false);
  const [showSplitPaymentModal, setShowSplitPaymentModal] = useState<boolean>(false);
  const [showSendPaymentLinkModal, setShowSendPaymentLinkModal] = useState<boolean>(false);
  const [showAddPaymentRecipetModal, setShowAddPaymentRecipetModal] = useState<boolean>(false);
  const [showAddPartialPaymentModal, setShowAddPartialPaymentModal] = useState<boolean>(false);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState<boolean>(false);
  const [vatBreakdownPayment, setVatBreakdownPayment] = useState<PaymentModel | null>(null);
  const [resendConfirmationPayment, setResendConfirmationPayment] = useState<
    PaymentModel | undefined
  >();

  const [activePayment, setActivePayment] = useState<PaymentModel>();
  const [activePaymentId, setActivePaymentId] = useState<string>('');
  const [detailPayment, setDetailPayment] = useState<PaymentModel | null>(null);
  const [checkingPaymobPaymentId, setCheckingPaymobPaymentId] = useState<string>('');
  const [switchingGatewayPaymentId, setSwitchingGatewayPaymentId] = useState<string>('');
  const [pendingGatewaySwitch, setPendingGatewaySwitch] = useState<{
    payment: PaymentModel;
    targetGateway: PaymentGateway;
  } | null>(null);
  const [paymentToMove, setPaymentToMove] = useState<PaymentModel | null>(null);
  const [isMovingPayment, setIsMovingPayment] = useState<boolean>(false);

  const movePaymentToReservation = (targetReservationId: string, comment: string) => {
    if (!paymentToMove) return;
    setIsMovingPayment(true);
    movePayment(paymentToMove.id, targetReservationId, comment)
      .then((response) => {
        if (isErrorInfo(response)) {
          birdToast.error(response.message || 'Failed to move payment');
          return;
        }
        birdToast.success(`Payment moved to reservation ${targetReservationId}`);
        setPaymentToMove(null);
        return onGetOneReservation(reservation.id);
      })
      .finally(() => setIsMovingPayment(false));
  };

  const activePaymentIdForHistory = detailPayment?.id || activePayment?.id || '';
  const collectedTotal = (reservation.paymentList || [])
    .filter((payment) => payment.status === PaymentStatus.COLLECTED)
    .reduce((sum, payment) => {
      const amount = payment.paymentAmount || 0;
      if (payment.direction === 'out') return sum - amount;
      return sum + (payment.currency === 'usd' ? amount * (reservation.usdRate || 1) : amount);
    }, 0);

  const switchPaymentGateway = (payment: PaymentModel, targetGateway: PaymentGateway) => {
    setSwitchingGatewayPaymentId(payment.id);

    const replacementPayment: PaymentUIModel = {
      reservationId: String(reservation.id),
      unitTypeId: payment.unitTypeId || reservation.unitTypeId || '',
      roomId: payment.roomId || reservation.roomId || '',
      dueDate: payment.dueDate,
      checkInDate: Number(reservation.checkInDate),
      checkOutDate: Number(reservation.checkOutDate),
      areaId: payment.areaId || reservation.area,
      userId: payment.userId || reservation.userId,
      document: payment.paymentItems,
      paymentItems: payment.paymentItems,
      direction: payment.direction,
      status: PaymentStatus.PENDING,
      collector: payment.collector,
      paymentType: payment.paymentType,
      paymentGateway: targetGateway,
      type: payment.type,
      paymentAmount: payment.paymentAmount,
      unitTypeGroupId: String(payment.unitTypeGroupId || reservation.unitTypeGroupId),
      landlordId: String(payment.landlordId || reservation.landlordId),
      propertyId: String(payment.propertyId || reservation.propertyId),
      source: String(payment.source || reservation.source),
      bankCharges: 0,
      bankChargesCurrency: payment.currency || 'egp',
      forexDifference: 0
    };

    createOnePayment(replacementPayment)
      .then((response: PaymentUIModel | ErrorInfo) => {
        if (isErrorInfo(response) || !response?.id) {
          birdToast.error('Failed to switch payment gateway');
          return;
        }

        const newPaymentId = response.id;
        const updatedReservation: Partial<ReservationDetailsUiModel> = {
          id: reservation.id,
          paymentIds: Array.isArray(reservation.paymentIds)
            ? [...reservation.paymentIds, newPaymentId]
            : [newPaymentId]
        };

        return updateOnePayment({ id: payment.id, status: PaymentStatus.CANCELED })
          .then(() => updateOneReservations(updatedReservation))
          .then(() => onGetOneReservation(reservation.id))
          .then(() =>
            birdToast.success(
              `Payment switched to ${targetGateway === PaymentGateway.GEIDEA ? 'Geidea' : 'Paymob'}`
            )
          );
      })
      .finally(() => {
        setSwitchingGatewayPaymentId('');
        setPendingGatewaySwitch(null);
      });
  };

  return (
    <>
      <AddPaymentModal
        config={{ showModal: showAddPaymentModal }}
        data={{ reservation }}
        eventHandlers={{
          closeHandler: () => {
            setShowAddPaymentModal(false);
          },
          addPaymentHandler: (payment) => {
            createOnePayment(payment)
              .then((response: PaymentUIModel | ErrorInfo) => {
                if (!isErrorInfo(response)) {
                  return response;
                }
              })
              .then((newPayment: PaymentUIModel | undefined) => {
                if (reservation && newPayment) {
                  let updatedReservation: Partial<ReservationDetailsUiModel> = {
                    id: reservation.id
                  };
                  if (Array.isArray(reservation.paymentIds)) {
                    updatedReservation = {
                      ...updatedReservation,
                      paymentIds: [...reservation.paymentIds, newPayment.id || '']
                    };
                  } else {
                    updatedReservation = {
                      ...updatedReservation,
                      paymentIds: [newPayment.id || '']
                    };
                  }
                  updateOneReservations(updatedReservation).then(() =>
                    onGetOneReservation(reservation.id).then(() => setShowAddPaymentModal(false))
                  );
                }
              });
          }
        }}
      />
      <SplitPaymentModal
        config={{ showModal: showSplitPaymentModal }}
        data={{
          reservation,
          sourcePayment: reservation?.paymentList?.find((payment) => payment.id === activePaymentId)
        }}
        eventHandlers={{
          closeHandler: () => {
            setActivePaymentId('');
            setShowSplitPaymentModal(false);
          },
          SplitPaymentHandler: (paymentSplitData: SplitPaymentModel) => {
            updateOnePayment(paymentSplitData.paymentToUpdate).then(() => {
              createOnePayment(paymentSplitData.paymentToAdd)
                .then((response: PaymentUIModel | ErrorInfo) => {
                  if (!isErrorInfo(response)) {
                    return response;
                  }
                })
                .then((newPayment: PaymentUIModel | undefined) => {
                  setShowSplitPaymentModal(false);
                  if (reservation && newPayment) {
                    let updatedReservation: Partial<ReservationDetailsUiModel> = {
                      id: reservation.id
                    };
                    if (Array.isArray(reservation.paymentIds)) {
                      updatedReservation = {
                        ...updatedReservation,
                        paymentIds: [...reservation.paymentIds, newPayment.id || '']
                      };
                    } else {
                      updatedReservation = {
                        ...updatedReservation,
                        paymentIds: [newPayment.id || '']
                      };
                    }
                    updateOneReservations(updatedReservation).then(() =>
                      onGetOneReservation(reservation.id).then(() => setShowAddPaymentModal(false))
                    );
                  }
                });
            });
          }
        }}
      />
      <GeneratePaymentLinkModal
        config={{ showModal: showGeneratePaymentLinkModal }}
        data={{ contactInfo: contactEmail }}
        eventHandlers={{
          closeHandler: () => {
            setActivePaymentId('');
            setShowGeneratePaymentLinkModal(false);
          },
          generatePaymentLinkHandler: ({ skipEmail, email }) => {
            generatePaymentLink({ paymentId: activePaymentId.toString(), skipEmail, email }).then(
              (response: any | ErrorInfo) => {
                if (!isErrorInfo(response)) {
                  birdToast.success('Payment Link Generated Successfully');
                  if (reservation) {
                    onGetOneReservation(reservation.id).then(() => {
                      setActivePaymentId('');
                      setShowGeneratePaymentLinkModal(false);
                    });
                  }
                  return response;
                } else {
                  birdToast.error('Payment Link failed To Generated');
                }
              }
            );
          }
        }}
      />
      <SendPaymentLinkModal
        config={{ showModal: showSendPaymentLinkModal }}
        data={{ contactInfo: contactEmail }}
        eventHandlers={{
          closeHandler: () => {
            setActivePaymentId('');
            setShowSendPaymentLinkModal(false);
          },
          sendPaymentLinkHandler: (email: string) => {
            sendPayment(activePaymentId, email).then((response: any | ErrorInfo) => {
              if (!isErrorInfo(response)) {
                birdToast.success('Payment Link Sent Successfully');
                setShowSendPaymentLinkModal(false);
                return response;
              } else {
                birdToast.error('Payment Link failed To be sent');
              }
            });
          }
        }}
      />
      <AddPaymentReceiptModal
        config={{
          showModal: showAddPaymentRecipetModal,
          userRole: undefined,
          userRoles: undefined
        }}
        data={{ status: activePayment?.status, paymentId: activePayment?.id }}
        eventHandlers={{
          closeHandler: () => {
            setShowAddPaymentRecipetModal(false);
          },
          addPaymentReceiptHandler: (
            asset: File,
            collectedAtDate: Date,
            status?: PaymentStatus,
            id?: string,
            skipEmail?: boolean,
            bankCharges?: number,
            bankChargesCurrency?: string,
            forexDifference?: number
          ) => {
            if (asset && id && reservation) {
              uploadPaymentReceipt(
                asset,
                collectedAtDate,
                id,
                reservation.id,
                status || PaymentStatus.COLLECTED,
                skipEmail || false,
                undefined,
                bankCharges,
                bankChargesCurrency,
                forexDifference
              ).then(() => {
                if (activePayment?.type === PaymentOccurrenceMode.ONE_TIME) {
                  trackEvent('payment_collected', {
                    amount:
                      reservation && reservation.paymentList
                        ? reservation.paymentList
                            .filter((payment) => payment.status === PaymentStatus.COLLECTED)
                            .reduce(
                              (prev, current) =>
                                current.direction === 'out'
                                  ? prev - ((current && current.paymentAmount) || 0)
                                  : prev + ((current && current.paymentAmount) || 0),
                              0
                            )
                        : 0,
                    currency: 'EGP',
                    reservation_id: reservation?.id?.toString()
                  });
                }
                onGetOneReservation(reservation.id);
                setShowAddPaymentRecipetModal(false);
              });
            }
          }
        }}
      />
      <AddPartialPaymentModal
        config={{ showModal: showAddPartialPaymentModal }}
        data={{
          paymentId: activePayment?.id,
          currency: activePayment?.currency,
          totalAmount: activePayment?.paymentAmount
        }}
        eventHandlers={{
          closeHandler: () => {
            setShowAddPartialPaymentModal(false);
          },
          addPartialPaymentHandler: (
            asset: File,
            partialAmount: number,
            collectedAtDate: Date,
            id?: string,
            skipEmail?: boolean,
            bankCharges?: number,
            bankChargesCurrency?: string,
            forexDifference?: number
          ) => {
            if (asset && id && reservation && partialAmount > 0) {
              uploadPaymentReceipt(
                asset,
                collectedAtDate,
                id,
                reservation.id,
                PaymentStatus.PARTIALLY_COLLECTED,
                skipEmail || false,
                partialAmount,
                bankCharges,
                bankChargesCurrency,
                forexDifference
              ).then(() => {
                onGetOneReservation(reservation.id);
                setShowAddPartialPaymentModal(false);
              });
            }
          }
        }}
      />

      {/* Payment Details Modal */}
      <PaymentDetailsModal
        showModal={showDetailsModal}
        payment={detailPayment}
        reservation={reservation}
        isPermited={isPermited}
        onClose={() => {
          setShowDetailsModal(false);
          setDetailPayment(null);
        }}
        onGetOneReservation={onGetOneReservation}
        onStatusChange={(payment, newStatus) => {
          setActivePayment({
            ...payment,
            status: newStatus
          });
          if (newStatus === PaymentStatus.PARTIALLY_COLLECTED) {
            setShowAddPartialPaymentModal(true);
          } else if (newStatus === PaymentStatus.COLLECTED) {
            setShowAddPaymentRecipetModal(true);
          } else if (newStatus === PaymentStatus.PENDING) {
            updateOnePayment({
              id: payment.id,
              status: newStatus
            })
              .then(() => onGetOneReservation(reservation.id))
              .finally(() => birdToast.success('Payment status updated to Pending successfully'));
          }
        }}
      />

      <PaymentVatBreakdownModal
        config={{ showModal: !!vatBreakdownPayment }}
        data={{ payment: vatBreakdownPayment }}
        eventHandlers={{ onClose: () => setVatBreakdownPayment(null) }}
      />

      <PaymentHistoryModal
        config={{ showModal: showPaymentHistoryModal }}
        data={{ paymentId: activePaymentIdForHistory }}
        eventHandlers={{ onClose: () => setShowPaymentHistoryModal(false) }}
      />

      <SwitchGatewayModal
        config={{
          showModal: !!pendingGatewaySwitch,
          isSwitching: !!switchingGatewayPaymentId
        }}
        data={{
          payment: pendingGatewaySwitch?.payment || null,
          targetGateway: pendingGatewaySwitch?.targetGateway || null
        }}
        eventHandlers={{
          closeHandler: () => setPendingGatewaySwitch(null),
          confirmHandler: () => {
            if (pendingGatewaySwitch) {
              switchPaymentGateway(
                pendingGatewaySwitch.payment,
                pendingGatewaySwitch.targetGateway
              );
            }
          }
        }}
      />

      {paymentToMove && (
        <MovePaymentModal
          config={{ isMoving: isMovingPayment }}
          data={{ paymentId: paymentToMove.id }}
          eventHandlers={{
            closeHandler: () => setPaymentToMove(null),
            confirmHandler: movePaymentToReservation
          }}
        />
      )}

      <ResendGuestEmailModal
        config={{
          showModal: !!resendConfirmationPayment,
          lockedPayment: resendConfirmationPayment
        }}
        data={{ reservation, guestEmail: contactEmail || reservation.primaryGuestEmail || '' }}
        eventHandlers={{ closeHandler: () => setResendConfirmationPayment(undefined) }}
      />

      <div className="flex items-center justify-between gap-[12px] flex-wrap">
        <h2 className="m-0 text-[clamp(18px,1.5vw,22px)] font-gilroy-bold text-neutral-500">
          Payments
        </h2>
        {hasAccess([
          PERMISSIONS_LIST.reservationsFullAccess,
          PERMISSIONS_LIST.reservationsModifyAccess,
          PERMISSIONS_LIST.paymentsCreateAccess
        ]) && (
          <button
            type="button"
            onClick={() => setShowAddPaymentModal(true)}
            className="inline-flex items-center gap-[6px] px-[16px] py-[10px] rounded-[8px] bg-neutral-500 text-neutral-0 text-[14px] font-gilroy-medium hover:bg-neutral-700 cursor-pointer"
          >
            <HugeiconsIcon icon={Add01Icon} size={16} />
            Add payment
          </button>
        )}
      </div>

      <div className="mt-[20px]">
        <PaymentsTable
          payments={reservation.paymentList || []}
          reservationSource={reservation.source}
          isPermited={isPermited}
          checkingPaymobPaymentId={checkingPaymobPaymentId}
          switchingGatewayPaymentId={switchingGatewayPaymentId}
          onViewDetails={(payment) => {
            setDetailPayment(payment);
            setShowDetailsModal(true);
          }}
          onSend={(payment) => {
            setActivePaymentId(payment.id);
            setShowSendPaymentLinkModal(true);
          }}
          onPartial={(payment) => {
            setActivePayment(payment);
            setActivePaymentId(payment.id);
            setShowAddPartialPaymentModal(true);
          }}
          onSplitPayment={(payment) => {
            setActivePaymentId(payment.id);
            setShowSplitPaymentModal(true);
          }}
          onGenerateLink={(payment) => {
            setActivePaymentId(payment.id);
            setShowGeneratePaymentLinkModal(true);
          }}
          onAddReceipt={(payment) => {
            setActivePayment(payment);
            setShowAddPaymentRecipetModal(true);
          }}
          onViewHistory={(payment) => {
            setActivePayment(payment);
            setShowPaymentHistoryModal(true);
          }}
          onViewVatBreakdown={(payment) => setVatBreakdownPayment(payment)}
          onResendConfirmation={(payment) => setResendConfirmationPayment(payment)}
          onCheckPaymobStatus={(payment) => {
            setCheckingPaymobPaymentId(payment.id);
            checkPaymobPaymentStatus(payment.id)
              .then((response) => {
                if (response?.paymobTransaction?.success === true) {
                  birdToast.success('Payment paid');
                } else if (response?.paymobTransaction?.success === false || response == null) {
                  birdToast.error('Payment not paid');
                } else {
                  birdToast.error(
                    'Failed to check payment status against Paymob, please contact support'
                  );
                }
              })
              .finally(() => setCheckingPaymobPaymentId(''));
          }}
          onSwitchGateway={(payment, targetGateway) =>
            setPendingGatewaySwitch({ payment, targetGateway })
          }
          onMoveToReservation={(payment) => setPaymentToMove(payment)}
        />
      </div>

      {collectedTotal !== 0 && (
        <div className="mt-[16px] pt-[16px] border-t border-neutral-500 flex justify-between text-[15px] font-gilroy-bold text-neutral-500">
          <span>Total received</span>
          <NumberFormat
            value={collectedTotal}
            displayType="text"
            thousandSeparator
            decimalScale={2}
            prefix="EGP "
          />
        </div>
      )}
    </>
  );
};
